/**
 * Persistance locale (§4, étape 8).
 *
 * Couche mince au-dessus de `localStorage` ; toute la validation vit dans
 * `core/save.ts`, qui est pur et testé sans navigateur.
 *
 * **`localStorage` peut lever, et pas seulement en navigation privée** : quota plein,
 * stockage désactivé par une politique d'entreprise, contexte non sécurisé. Un jeu qui
 * plante à l'écriture d'une sauvegarde est bien pire qu'un jeu qui ne sauvegarde pas :
 * chaque accès est donc protégé, et l'échec est silencieux côté joueur.
 *
 * **L'écriture est étalée dans le temps.** Le tick tourne à 10 Hz ; sérialiser la ville
 * cent fois par seconde bloquerait le fil principal sur du JSON à chaque frame. On écrit
 * au plus une fois par `SAVE_INTERVAL_MS`, plus une écriture garantie quand l'onglet
 * passe en arrière-plan — c'est le seul moment où l'on est sûr de perdre la main.
 */

import { deserialize, openEnvelope, repackFootprints, sealEnvelope, serialize } from '@/core/save';
import { footprintCells, footprintOf } from '@/core/gameState';
import { pruneRoads, rebuildNetwork, servedCells } from '@/core/roads';
import type { LoadOutcome } from '@/core/save';
import type { CitySnapshot } from '@/core/gameState';

export const STORAGE_KEY = 'villopia:save:v1';

/*
  ── Double emplacement (A.2) ──────────────────────────────────────────────────

  Un onglet tué en pleine écriture laisse un texte tronqué SOUS LA MÊME CLÉ que la
  bonne copie qu'il vient d'écraser : avec un seul emplacement, la partie est perdue
  quoi que fasse la relecture. Les écritures ALTERNENT donc entre deux clés — au pire,
  la troncature détruit l'avant-dernière sauvegarde, jamais la dernière ET
  l'avant-dernière. La relecture prend la plus récente des copies dont le sceau (CRC,
  `core/save.ts`) est intact.

  `STORAGE_KEY` (v1, non scellée) n'est plus écrite mais reste LUE : c'est la
  migration des joueurs existants. Elle reste aussi effacée par la remise à zéro.
*/
export const SLOT_KEYS = ['villopia:save:v2:a', 'villopia:save:v2:b'] as const;

/** Écriture au plus toutes les 5 s : imperceptible pour le joueur, indolore pour le fil. */
export const SAVE_INTERVAL_MS = 5000;

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Le réseau tient-il en un seul morceau ?
 *
 * Un réseau en plusieurs composantes n'est pas un réseau : aucun véhicule ne peut le
 * parcourir, aucun carrefour ne s'y forme, et les cases isolées se rendent en impasses
 * orientées au hasard. C'est le symptôme exact des villes sauvegardées avant le
 * correctif du 17 août.
 */
function isSingleComponent(roads: ReadonlySet<string>): boolean {
  const first = [...roads][0];
  if (first === undefined) return true;

  const seen = new Set([first]);
  const queue = [first];

  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let head = 0; head < queue.length; head++) {
    const current = queue[head];
    if (current === undefined) continue;
    const parts = current.split(':');
    const col = Number(parts[0]);
    const row = Number(parts[1]);

    for (const [dc, dr] of [
      [0, -1],
      [1, 0],
      [0, 1],
      [-1, 0],
    ] as const) {
      const key = `${String(col + dc)}:${String(row + dr)}`;
      if (roads.has(key) && !seen.has(key)) {
        seen.add(key);
        queue.push(key);
      }
    }
  }

  return seen.size === roads.size;
}

/**
 * Prochain emplacement à écrire — l'état que `loadSnapshot` lègue à l'ordonnanceur.
 *
 * On écrit PAR-DESSUS la copie la plus ancienne, jamais par-dessus la plus récente :
 * si cette écriture-là est interrompue, la meilleure copie existante survit. Variable
 * de module et non paramètre, parce que `loadSnapshot` et `createSaveScheduler` sont
 * appelés à deux endroits distincts de `game.ts` et que ce détail d'aiguillage
 * n'appartient à aucun des deux.
 */
let nextSlot = 0;

/** Relit les deux emplacements scellés, et la clé v1 en repli de migration. */
function readBestRaw(): string | null {
  const candidates = SLOT_KEYS.map((key, index) => {
    const envelope = openEnvelope(safeRead(key));
    if (!envelope.ok) return null;

    /*
      `updatedAt` est relu ici SEULEMENT pour départager deux copies valides — jamais
      comme source de temps (le contrat de `deserialize` reste entier). Un JSON
      illisible malgré un sceau intact n'arrive en pratique jamais ; le repli 0 le
      classe simplement derrière toute copie datée.
    */
    let updatedAt = 0;
    try {
      const parsed: unknown = JSON.parse(envelope.json);
      if (typeof parsed === 'object' && parsed !== null && 'updatedAt' in parsed) {
        const value: unknown = parsed.updatedAt;
        if (typeof value === 'number' && Number.isFinite(value)) updatedAt = value;
      }
    } catch {
      return null;
    }

    return { index, json: envelope.json, updatedAt };
  }).filter((candidate) => candidate !== null);

  candidates.sort((a, b) => b.updatedAt - a.updatedAt);
  const best = candidates[0];

  if (best !== undefined) {
    if (candidates.length < SLOT_KEYS.length) {
      // Une copie a été rejetée (troncature, altération) : c'est exactement le cas
      // que le double emplacement existe pour absorber. Il se signale.
      console.warn('[villopia] une copie de sauvegarde était corrompue ; l’autre a servi.');
    }
    nextSlot = 1 - best.index;
    return best.json;
  }

  // Aucune copie scellée : joueur d'avant la v2, ou première visite.
  return safeRead(STORAGE_KEY);
}

export function loadSnapshot(now: number): LoadOutcome {
  const outcome = deserialize(readBestRaw(), now);

  // Les emprises sont arrivées après les premières sauvegardes : une ville enregistrée
  // avant contient des bâtiments 2 × 2 posés sur une seule case, donc superposés depuis
  // la mise à jour. On répare au chargement, sans jamais perdre un bâtiment.
  const repacked = outcome.snapshot.islands.map((island) => repackFootprints(island, footprintOf));
  const moved = repacked.reduce((sum, result) => sum + result.moved, 0);

  if (moved > 0) {
    console.warn(`[villopia] ${String(moved)} bâtiment(s) déplacé(s) : emprises superposées.`);
  }

  /*
    Réseau routier des villes d'avant les routes.

    Sans cette reconstruction, la feature est invisible pour tout joueur qui a déjà
    joué : les routes ne se tracent qu'à la pose, et une ville existante n'en a aucune.
    On rejoue le raccordement dans l'ordre de pose, donc le réseau obtenu est celui
    qu'on aurait eu naturellement — pas une grille plaquée après coup.
  */
  const islands = repacked.map((result) => {
    if (result.island.buildings.length === 0) return result.island;

    /*
      ── DEUX CAS DE RECONSTRUCTION, et le second est arrivé après coup ────────

      1. **Aucune route.** Ville d'avant l'arrivée des routes : la feature serait
         strictement invisible pour tout joueur déjà installé.

      2. **Un réseau FRAGMENTÉ.** C'est le legs de l'audit du 17 août. Jusque-là, un
         raccordement impossible posait silencieusement une case de bitume isolée : les
         villes sauvegardées contiennent donc des flaques de masque 0, des composantes
         disjointes et des bâtiments enclavés. Le moteur corrigé ne les répare pas tout
         seul — les routes sont un ÉTAT, pas une dérivée — et le joueur garderait sa
         ville cassée indéfiniment, sans jamais voir un véhicule ni un feu.

      Livrer le correctif sans cette migration reviendrait à ne rien livrer pour ceux qui
      jouent déjà, c'est-à-dire pour les seuls joueurs qui comptent aujourd'hui.
    */
    const existing = new Set(result.island.roads);
    const fragmented = existing.size > 0 && !isSingleComponent(existing);

    if (result.island.roads.length > 0 && !fragmented) return result.island;

    const roads = rebuildNetwork({
      grid: result.island.grid,
      buildings: result.island.buildings.map((building) => ({
        placedAt: building.placedAt,
        cells: footprintCells(building.type, building.col, building.row),
      })),
    });

    console.warn(
      fragmented
        ? `[villopia] réseau fragmenté (${String(existing.size)} case(s), plusieurs morceaux) : reconstruit en ${String(roads.length)} case(s).`
        : `[villopia] réseau routier reconstruit : ${String(roads.length)} case(s).`,
    );
    return { ...result.island, roads };
  });

  /*
    Élagage au chargement : les villes existantes traînent des rues qui ne desservent
    plus rien — bâtiments vendus, déplacés, ou raccordements devenus redondants au fil
    des poses. Sans ce passage, le gaspillage accumulé ne se résorbe jamais.
  */
  const tidied = islands.map((island) => {
    if (island.roads.length === 0) return island;

    const served = servedCells(
      island.buildings.map((building) => ({
        cells: footprintCells(building.type, building.col, building.row),
      })),
    );
    const pruned = pruneRoads(island.roads, served);

    if (pruned.length !== island.roads.length) {
      console.warn(
        `[villopia] ${String(island.roads.length - pruned.length)} case(s) de route élaguée(s).`,
      );
    }
    return { ...island, roads: pruned };
  });

  return { ...outcome, snapshot: { ...outcome.snapshot, islands: tidied } };
}

/**
 * Efface la partie — les DEUX emplacements scellés et la clé v1 héritée.
 *
 * La remise à zéro faisait `removeItem(STORAGE_KEY)` en direct dans `game.ts` : avec
 * l'arrivée des emplacements, elle n'aurait effacé que la copie de migration et la
 * ville serait revenue au rechargement. La liste des clés appartient à ce module ;
 * l'effacement aussi.
 */
export function clearSnapshot(): void {
  try {
    for (const key of [...SLOT_KEYS, STORAGE_KEY]) localStorage.removeItem(key);
  } catch {
    // Stockage indisponible : il n'y avait alors rien de sauvegardé à effacer.
  }
}

export interface SaveScheduler {
  /** Enregistre le snapshot courant ; l'écriture réelle peut être différée. */
  schedule(snapshot: CitySnapshot, now: number): void;
  /** Écrit immédiatement, quoi qu'il arrive. */
  flush(): void;
  dispose(): void;
}

export interface SaveSchedulerOptions {
  /**
   * Appelé à la PREMIÈRE écriture ratée d'une série.
   *
   * Une seule fois par incident : répéter l'alerte dix fois par seconde en
   * transformerait le message en bruit, et un joueur qui l'a lue n'a pas besoin
   * qu'on le lui rappelle. Le compteur se réarme dès qu'une écriture réussit — un
   * nouvel échec plus tard est un nouvel incident.
   */
  readonly onWriteFailure?: () => void;
}

export function createSaveScheduler(options: SaveSchedulerOptions = {}): SaveScheduler {
  let pending: CitySnapshot | null = null;
  let lastWriteAt = 0;
  let failureReported = false;

  const write = (): void => {
    if (pending === null) return;

    const slotKey = SLOT_KEYS[nextSlot] ?? SLOT_KEYS[0];
    const written = safeWrite(
      slotKey,
      sealEnvelope(serialize({ ...pending, updatedAt: Date.now() })),
    );

    if (!written) {
      /*
        `pending` est CONSERVÉ.

        Le défaut n'était pas seulement le silence : `pending` était vidé quoi qu'il
        arrive, si bien qu'une écriture ratée perdait l'état et que la tentative
        suivante n'avait plus rien à écrire. Le jeu ne se rattrapait jamais, même une
        fois la place revenue. En le gardant, la prochaine occasion réussit.
      */
      if (!failureReported) {
        failureReported = true;
        options.onWriteFailure?.();
      }
      return;
    }

    failureReported = false;
    pending = null;
    // L'alternance ne progresse que sur une écriture RÉUSSIE : après un échec, la
    // tentative suivante revise le même emplacement, jamais la dernière bonne copie.
    nextSlot = 1 - nextSlot;
  };

  /**
   * `visibilitychange` et non `beforeunload` : sur mobile, une application balayée hors
   * de l'écran ne déclenche jamais `beforeunload`. `visibilitychange` est le seul
   * événement fiable pour « le joueur vient de partir ».
   */
  const onHidden = (): void => {
    if (document.visibilityState === 'hidden') write();
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', write);

  return {
    schedule(snapshot, now) {
      pending = snapshot;
      if (now - lastWriteAt >= SAVE_INTERVAL_MS) {
        lastWriteAt = now;
        write();
      }
    },
    flush: write,
    dispose() {
      write();
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', write);
    },
  };
}
